import React from "react";
import { PALE_LOADING_CARD_FILL, PALE_LOADING_ROW_FILL, PALE_DELIVERY_FILL } from "./palette.js";
import { CalculationLegend, CompactCalculationBreakdown } from "../billing/CalculationBreakdown.jsx";
import { formatBillingDuration } from "../../utils/billingCalculation.js";
import { consolidationCalculationMills } from "../../utils/consolidationMillCalculation.js";
import BillableHoursSummary from "./BillableHoursSummary.jsx";
import { dompeurTonnesParts, isDompeurSheet } from "../../utils/consolidationDompeur.js";
import { numberLabel } from "../../utils/consolidationPresentation.js";

// Camion dompeur : seules les tonnes livrées sont facturées, par entreprise ; aucune heure n'est montrée.
function DompeurTonnesSummary({ sheet }) {
  const parts = dompeurTonnesParts(sheet, null);
  return <section aria-label="Tonnes par entreprise" className="min-w-0 rounded-[4px] border border-slate-200 border-t-2 border-t-accent bg-white">
    <h3 className="px-2 py-1 text-xs font-semibold">Tonnes par entreprise · dompeur</h3>
    {parts.length ? <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,14rem),1fr))] gap-1.5 px-2 pb-1.5">
      {parts.map(part => <div key={part.key} style={{ backgroundColor: PALE_LOADING_CARD_FILL }} className="grid min-w-0 rounded-[4px] border border-slate-300 px-2 py-1 text-slate-700">
        <span className="break-words text-xs font-semibold">{part.label}</span>
        <strong className="whitespace-nowrap text-sm tabular-nums">{numberLabel(part.tons, 3)} t</strong>
      </div>)}
    </div> : <p className="px-3 pb-2 text-xs text-slate-600">Aucune tonne livrée.</p>}
  </section>;
}

export default function Calculation({ value, date, sheet, summary = false, pending = false, awaitingInput = false, error, onRetry, resultsHidden = false }) {
  const [detail, setDetail] = React.useState(null);
  const mills = React.useMemo(() => consolidationCalculationMills(value, date, sheet), [value, date, sheet]);
  if (!value && !summary) return null;
  const operational = value?.format_version === 2;
  const standaloneInspection = [...mills.values()].some((mill) => mill.stopGroups.some((group) => group.items.some((item) => item.calculation_details?.kind === "inspection")));
  const kinds = (value?.charges || []).filter((charge) => charge.kind !== "inspection" || standaloneInspection).map((charge) => charge.kind);
  if (summary) return isDompeurSheet(sheet) ? <DompeurTonnesSummary sheet={sheet} /> : <BillableHoursSummary mills={value ? mills : null}
    pending={pending} awaitingInput={awaitingInput} error={error} onRetry={onRetry} resultsHidden={resultsHidden} />;
  return <section aria-label="Répartition calculée" style={{ "--consolidation-loading-row-fill": PALE_LOADING_ROW_FILL, "--consolidation-delivery-fill": PALE_DELIVERY_FILL }} className="min-w-0 rounded-[4px] border border-slate-200 bg-white">
    <header className="flex flex-wrap items-center gap-x-4 gap-y-1 px-2 py-1.5">
      <h3 className="text-sm font-semibold" title="Durées des cases arrondies à la minute">Répartition calculée · {formatBillingDuration(value.net_seconds)}</h3>
      <CalculationLegend kinds={kinds} />
    </header>
    <div className="divide-y divide-slate-200 border-t border-slate-200">
      {[...mills].map(([key, mill]) => <section key={key} aria-label={`Répartition · ${mill.label}`} className="min-w-0">
        <CompactCalculationBreakdown items={mill.items} label={mill.label} showAmounts={!operational}
          stopGroups={mill.stopGroups}
          detail={detail?.millKey === key ? detail : null} onDetailChange={(next) => setDetail(next ? { ...next, millKey: key } : null)} />
      </section>)}
    </div>
  </section>;
}
